import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  Paper,
  CircularProgress,
  LinearProgress,
  Divider,
  InputAdornment,
  Tooltip,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Forward as ForwardIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Mic as AudioIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 16,
      maxWidth: 540,
      width: "100%",
      background: theme.palette.type === "dark"
        ? "linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)"
        : "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
    },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5, 3, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& svg": {
      color: theme.palette.primary.main,
    },
  },
  titleText: {
    fontWeight: 700,
    fontSize: "1.15rem",
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
    overflowY: "auto",
    maxHeight: "65vh",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  // ── Preview da mensagem ──
  previewBox: {
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.05)"
      : "rgba(0,0,0,0.04)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.5, 2),
  },
  previewLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.75),
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  previewContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.primary,
    fontSize: "0.9rem",
    wordBreak: "break-word",
  },
  previewMediaIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.4rem",
  },
  // ── Busca ──
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
    },
  },
  // ── Lista de resultados ──
  resultsList: {
    maxHeight: 200,
    overflowY: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.primary.light,
      borderRadius: 3,
    },
  },
  resultItem: {
    cursor: "pointer",
    transition: "background 0.15s",
    borderRadius: 8,
    margin: theme.spacing(0.25, 0.5),
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.08)"
        : "rgba(0,0,0,0.04)",
    },
  },
  resultItemSelected: {
    background: theme.palette.type === "dark"
      ? "rgba(99,102,241,0.15)"
      : "rgba(99,102,241,0.08)",
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(99,102,241,0.22)"
        : "rgba(99,102,241,0.14)",
    },
  },
  selectedCheckIcon: {
    color: theme.palette.primary.main,
  },
  // ── Chips de selecionados ──
  chipsSection: {},
  chipsSectionLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    minHeight: 52,
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.03)"
      : "rgba(0,0,0,0.02)",
  },
  chip: {
    borderRadius: 8,
    fontWeight: 500,
    background: theme.palette.type === "dark"
      ? "rgba(99,102,241,0.25)"
      : "rgba(99,102,241,0.12)",
    color: theme.palette.type === "dark" ? "#a5b4fc" : "#4338ca",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(99,102,241,0.4)" : "rgba(99,102,241,0.3)"}`,
    "& .MuiChip-deleteIcon": {
      color: theme.palette.type === "dark" ? "#a5b4fc" : "#6366f1",
      "&:hover": { color: theme.palette.error.main },
    },
  },
  emptyChips: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    fontStyle: "italic",
    margin: "auto",
  },
  // ── Progresso ──
  progressBox: {
    borderRadius: 10,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.5, 2),
    background: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.04)"
      : "rgba(0,0,0,0.03)",
  },
  progressLabel: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  linearProgress: {
    height: 8,
    borderRadius: 4,
  },
  // ── Erros ──
  failedBox: {
    borderRadius: 10,
    border: `1px solid ${theme.palette.error.light}`,
    background: theme.palette.type === "dark"
      ? "rgba(239,68,68,0.08)"
      : "rgba(239,68,68,0.05)",
    padding: theme.spacing(1.5, 2),
  },
  failedTitle: {
    color: theme.palette.error.main,
    fontWeight: 600,
    fontSize: "0.85rem",
    marginBottom: theme.spacing(0.5),
  },
  failedItem: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(1),
    borderLeft: `2px solid ${theme.palette.error.light}`,
    marginBottom: 4,
  },
  // ── Ações ──
  dialogActions: {
    padding: theme.spacing(1.5, 3, 2.5),
    gap: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  forwardButton: {
    borderRadius: 10,
    fontWeight: 600,
    padding: theme.spacing(0.9, 2.5),
    textTransform: "none",
    fontSize: "0.95rem",
    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
    },
    "&:disabled": {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled,
    },
  },
  cancelButton: {
    borderRadius: 10,
    fontWeight: 500,
    textTransform: "none",
    fontSize: "0.95rem",
  },
  noResults: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
    fontSize: "0.85rem",
  },
  searchingIndicator: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
}));

// Ícone de mídia conforme tipo
const MediaIcon = ({ mediaType, className }) => {
  if (mediaType === "image") return <ImageIcon className={className} />;
  if (mediaType === "video") return <VideoIcon className={className} />;
  if (mediaType === "audio" || mediaType === "ptt") return <AudioIcon className={className} />;
  return <FileIcon className={className} />;
};

// Gera iniciais para avatar
const getInitials = (name = "") =>
  name.trim().split(" ").slice(0, 2).map((n) => n[0]?.toUpperCase() || "").join("");

// Gera cor determinística pelo nome
const getAvatarColor = (name = "") => {
  const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#14b8a6", "#f59e0b", "#3b82f6", "#10b981"];
  let hash = 0;
  for (let c of name) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffffff;
  return colors[Math.abs(hash) % colors.length];
};

const ForwardMessageModal = ({ open, onClose, message }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const [failedContacts, setFailedContacts] = useState([]);
  const [isDone, setIsDone] = useState(false);
  const [ticketWhatsappId, setTicketWhatsappId] = useState(null);

  // Buscar contatos conforme digitação
  const searchContacts = useCallback(async (term) => {
    if (!term || term.trim().length < 2) {
      setContacts([]);
      return;
    }
    setIsSearching(true);
    try {
      const { data } = await api.get("/contacts", {
        params: { searchParam: term, pageNumber: 1 },
      });
      setContacts(data.contacts || []);
    } catch (err) {
      toastError(err);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce de 400ms
  useEffect(() => {
    const timer = setTimeout(() => searchContacts(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm, searchContacts]);

  // Resetar ao fechar / buscar ticket ao abrir
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setContacts([]);
      setSelectedContacts([]);
      setProgress(0);
      setProgressLabel("");
      setFailedContacts([]);
      setIsDone(false);
      setIsSending(false);
      setTicketWhatsappId(null);
    } else if (open && message?.ticketId) {
      // Buscar o ticket para obter o whatsappId
      api.get(`/tickets/${message.ticketId}`)
        .then(({ data }) => {
          setTicketWhatsappId(data.whatsappId);
        })
        .catch(() => {
          // Se falhar, o backend vai usar o whatsapp do ticket da mensagem original
          console.warn("[ForwardMessageModal] Não foi possível obter o whatsappId do ticket.");
        });
    }
  }, [open, message]);

  const isSelected = (contact) =>
    selectedContacts.some((c) => c.id === contact.id);

  const toggleContact = (contact) => {
    if (isSelected(contact)) {
      setSelectedContacts((prev) => prev.filter((c) => c.id !== contact.id));
    } else {
      setSelectedContacts((prev) => [...prev, contact]);
    }
  };

  const removeContact = (contactId) => {
    setSelectedContacts((prev) => prev.filter((c) => c.id !== contactId));
  };

  const handleSend = async () => {
    if (selectedContacts.length === 0) return;

    setIsSending(true);
    setProgress(0);
    setFailedContacts([]);
    setIsDone(false);

    const total = selectedContacts.length;
    setProgressLabel(
      i18n.t("forwardMessageModal.sending", { current: 0, total })
    );

    try {
      const { data } = await api.post(`/messages/${message.id}/forward`, {
        contactIds: selectedContacts.map((c) => c.id),
        whatsappId: ticketWhatsappId,
      });

      const { sent, failed } = data;

      if (failed && failed.length > 0) {
        setFailedContacts(failed);
        setProgressLabel("");
        setProgress(100);
        setIsDone(true);

        if (sent > 0) {
          toast.warning(
            i18n.t("forwardMessageModal.partialSuccess", {
              sent,
              failed: failed.length,
            }),
            { autoClose: 5000 }
          );
        } else {
          toast.error(i18n.t("forwardMessageModal.error"));
        }
      } else {
        setProgress(100);
        setProgressLabel("");
        setIsDone(true);
        toast.success(
          i18n.t("forwardMessageModal.success", { sent }),
          { autoClose: 4000 }
        );
        setTimeout(() => onClose(), 1800);
      }
    } catch (err) {
      toastError(err);
      setIsSending(false);
    } finally {
      setIsSending(false);
    }
  };

  const hasMedia = message && !!message.mediaUrl;
  const mediaType = message?.mediaType;
  const messageBody = message?.body;

  return (
    <Dialog
      open={open}
      onClose={!isSending ? onClose : undefined}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
    >
      {/* ── Título ── */}
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <ForwardIcon />
        <Typography className={classes.titleText}>
          {i18n.t("forwardMessageModal.title")}
        </Typography>
      </DialogTitle>

      {/* ── Conteúdo ── */}
      <DialogContent className={classes.dialogContent}>

        {/* Preview da mensagem */}
        <Box className={classes.previewBox}>
          <Typography className={classes.previewLabel}>
            📋 {i18n.t("forwardMessageModal.preview")}
          </Typography>
          <Box className={classes.previewContent}>
            {hasMedia && (
              <MediaIcon mediaType={mediaType} className={classes.previewMediaIcon} />
            )}
            <span>
              {hasMedia && !messageBody
                ? i18n.t("forwardMessageModal.mediaMessage")
                : messageBody || i18n.t("forwardMessageModal.mediaMessage")}
            </span>
          </Box>
        </Box>

        {/* Campo de busca */}
        <TextField
          className={classes.searchField}
          label={i18n.t("forwardMessageModal.searchPlaceholder")}
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isSending}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: isSearching ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Lista de resultados */}
        {contacts.length > 0 && (
          <Paper variant="outlined" className={classes.resultsList}>
            <List dense disablePadding>
              {contacts.map((contact) => {
                const selected = isSelected(contact);
                return (
                  <ListItem
                    key={contact.id}
                    className={`${classes.resultItem} ${selected ? classes.resultItemSelected : ""}`}
                    onClick={() => !isSending && toggleContact(contact)}
                    button
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={contact.profilePicUrl || ""}
                        style={{
                          width: 36,
                          height: 36,
                          fontSize: "0.85rem",
                          background: getAvatarColor(contact.name),
                        }}
                      >
                        {!contact.profilePicUrl && getInitials(contact.name)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {contact.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          {contact.number || contact.email || ""}
                        </Typography>
                      }
                    />
                    {selected && (
                      <ListItemSecondaryAction>
                        <CheckCircleIcon
                          className={classes.selectedCheckIcon}
                          fontSize="small"
                        />
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}

        {/* Sem resultados */}
        {!isSearching && searchTerm.length >= 2 && contacts.length === 0 && (
          <Typography className={classes.noResults}>
            Nenhum contato encontrado para "{searchTerm}"
          </Typography>
        )}

        <Divider />

        {/* Contatos selecionados */}
        <Box className={classes.chipsSection}>
          <Typography className={classes.chipsSectionLabel}>
            👥 {i18n.t("forwardMessageModal.selectedContacts")} ({selectedContacts.length})
          </Typography>
          <Box className={classes.chipsContainer}>
            {selectedContacts.length === 0 ? (
              <Typography className={classes.emptyChips}>
                {i18n.t("forwardMessageModal.noContacts")}
              </Typography>
            ) : (
              selectedContacts.map((contact) => (
                <Chip
                  key={contact.id}
                  className={classes.chip}
                  size="small"
                  avatar={
                    <Avatar
                      src={contact.profilePicUrl || ""}
                      style={{
                        background: getAvatarColor(contact.name),
                        fontSize: "0.7rem",
                      }}
                    >
                      {!contact.profilePicUrl && getInitials(contact.name)}
                    </Avatar>
                  }
                  label={contact.name}
                  onDelete={!isSending ? () => removeContact(contact.id) : undefined}
                />
              ))
            )}
          </Box>
        </Box>

        {/* Barra de progresso durante envio */}
        {isSending && (
          <Box className={classes.progressBox}>
            <Typography className={classes.progressLabel}>
              {progressLabel || `Enviando para ${selectedContacts.length} contato(s)...`}
            </Typography>
            <LinearProgress
              className={classes.linearProgress}
              variant={progress === 0 ? "indeterminate" : "determinate"}
              value={progress}
              color="primary"
            />
          </Box>
        )}

        {/* Contatos com falha */}
        {isDone && failedContacts.length > 0 && (
          <Box className={classes.failedBox}>
            <Typography className={classes.failedTitle}>
              ⚠️ {i18n.t("forwardMessageModal.failedContacts")}
            </Typography>
            {failedContacts.map((f) => (
              <Typography key={f.contactId} className={classes.failedItem}>
                {f.contactName} — {f.reason}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>

      {/* ── Ações ── */}
      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.cancelButton}
          onClick={onClose}
          disabled={isSending}
          color="default"
        >
          {i18n.t("forwardMessageModal.cancel")}
        </Button>
        <Button
          className={classes.forwardButton}
          onClick={handleSend}
          disabled={isSending || selectedContacts.length === 0 || isDone}
          startIcon={
            isSending ? (
              <CircularProgress size={16} style={{ color: "#fff" }} />
            ) : (
              <SendIcon />
            )
          }
          variant="contained"
          disableElevation
        >
          {isSending
            ? "Enviando..."
            : `${i18n.t("forwardMessageModal.forwardButton")}${
                selectedContacts.length > 0 ? ` (${selectedContacts.length})` : ""
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForwardMessageModal;
